import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
// import css last
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useDirectSpeech } from '../../../../components/DirectSpeech/DirectSpeechProvider';
import { Icon } from '../../../../components/Icon';
import { MessagePopover } from '../../../../components/Input/__internal__/MessagePopover';
import { OptionPopper } from '../../../../components/OptionPopper';
import { GlobalSettingsKeys } from '../../../../components/Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../../../components/Settings/GlobalSettingsProvider';
import { setAlpha } from '../../../../design-tokens';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { createUseHtmlId } from '../../../../hooks/useHtmlId';
import { isTruncated } from '../../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../../locale';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../../theme';
import { markEvent } from '../../../../util/events';
import { setRef } from '../../../../util/ref-util';
import { postfixTestId } from '../../../../util/testId';
import bundle from '../../NovaComponents-CatalogSearch-gui-icu.properties';
import cn from './CatalogInput.css.json';
import './CatalogInput.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isCustomMessage = message => message?.message !== undefined;
const useListId = createUseHtmlId('CatalogInput-SuggestionList');
const useListItemId = createUseHtmlId('CatalogInput-SuggestionListItem');
const useValidationMessageId = createUseHtmlId('CatalogInput-ValidationMessage');
const logger = getLogger('CatalogSearchInput');
// There is no global setting for a user-configured double-click delay, so match the double-click delay that table uses to
// determine the difference between a single and double click
// https://github.com/sas-institute-rnd-product/design-system-filament-nova/blob/c810b8c8150cb86c11f273250079154ec9f61f8d/src/nova-commons/src/hooks/useSingleAndDoubleClick/useSingleAndDoubleClick.ts#L122
const DOUBLE_CLICK_DELAY = 200;
const useTextLocalizer = createUseTextLocalizer(bundle);
export const CatalogInput = /*#__PURE__*/forwardRef(function CatalogBaseInput({
  onHistoryItemDoubleClick,
  ...props
}, ref) {
  const suggestionListRef = useRef(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(undefined);
  const [hasHover, setHasOver] = useState(false);
  const localize = useTextLocalizer();
  const editable = props.editable ?? true;
  const [isFirstFocus, setIsFirstFocus] = useState(true);
  const {
    suggestionItems,
    onSuggestionItemSelected
  } = props;
  const propsOnCommit = props.onCommit;
  const propsOnChange = props.onChange;
  const propsOnCancel = props.onCancel;
  const propsOnFocus = props.onFocus;
  const propsOnMouseDown = props.onMouseDown;
  const propsOnBlur = props.onBlur;
  const propsValue = props.value;
  const commitOnEnterInDialog = props.commitOnEnterInDialog !== undefined ? props.commitOnEnterInDialog : true;
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false);
  const propsSuggestionsDisplay = props.suggestionsDisplay || 'always';

  // this is for determining the focus index, which is based on the list with *no separators*
  const itemsNoSeparators = useMemo(() => {
    return suggestionItems?.filter(si => !si.separator);
  }, [suggestionItems]);
  const isRTL = useIsRTL();
  const closeSuggestions = useCallback(() => {
    setFocusedIndex(undefined);
    setShouldShowSuggestions(false);
  }, [setFocusedIndex, setShouldShowSuggestions]);
  const noSeparatorIndexToSuggestionIndex = useCallback(selectedIndex => {
    if (!itemsNoSeparators || !suggestionItems || selectedIndex >= itemsNoSeparators.length || selectedIndex < 0) {
      return;
    }
    return suggestionItems.indexOf(itemsNoSeparators[selectedIndex]);
  }, [itemsNoSeparators, suggestionItems]);
  const selectSuggestionHandler = useCallback(selectedIndex => {
    const suggestionIndex = noSeparatorIndexToSuggestionIndex(selectedIndex);
    if (suggestionIndex === undefined) {
      return;
    }
    onSuggestionItemSelected?.({
      index: suggestionIndex
    });
    const activeSuggestion = suggestionItems?.[suggestionIndex];
    if (activeSuggestion?.suggestionType !== 'catalog') {
      // keep the suggestions popper open for suggestions that are not history or help suggestions
      closeSuggestions();
    }
  }, [noSeparatorIndexToSuggestionIndex, onSuggestionItemSelected, closeSuggestions, suggestionItems]);
  const refInput = useRef(null);
  const setRefInput = useCallback(input => {
    refInput.current = input;
    setRef(ref, input);
  }, [ref]);
  const refList = useRef(null);
  useLayoutEffect(() => {
    if (shouldShowSuggestions && suggestionListRef?.current && refInput.current && refList.current) {
      suggestionListRef.current.style.maxWidth = `${refInput.current.getBoundingClientRect().width}px`;
      refList.current.style.maxWidth = `${refInput.current.getBoundingClientRect().width}px`;
    }
  }, [shouldShowSuggestions, suggestionListRef, refInput, refList]);
  const onBlur = useCallback(e => {
    const relatedToSuggestionList = suggestionListRef.current?.contains(e.relatedTarget) || suggestionListRef.current?.contains(e.target);
    if (!relatedToSuggestionList) {
      closeSuggestions();

      // Added for NOVACOMMONS-966, text remains selected when focus out via tab navigation
      !editable && window.getSelection().empty();
      if (focusedIndex === undefined || focusedIndex === null) {
        propsOnCommit?.({
          value: propsValue,
          reason: 'blur'
        });
      }
      propsOnBlur?.();
      setHasOver(false);
      setHasFocus(false);
    }
  }, [editable, propsOnBlur, propsOnCommit, focusedIndex, propsValue, closeSuggestions]);
  const handleFocusOnInput = useCallback(() => {
    setHasFocus(true);
    if (propsSuggestionsDisplay === 'always' || propsSuggestionsDisplay === 'secondFocus' && !isFirstFocus) {
      setShouldShowSuggestions(true);
    }
    if (isFirstFocus) {
      setIsFirstFocus(false);
    }
    propsOnFocus?.();
  }, [isFirstFocus, propsSuggestionsDisplay, propsOnFocus]);
  const onFocus = useCallback(e => {
    if (refInput.current && e.target !== refInput.current) {
      refInput.current.focus();
      return;
    }

    // that e.relatedTarget is null indicates the focus comes from outside of current page, and
    // the action should not be taken in that case.
    if (e.relatedTarget !== null) {
      handleFocusOnInput();
    }
  }, [handleFocusOnInput]);
  const getNextIndex = useCallback((suggItems, prevIndex, direction) => {
    if (!suggItems) {
      return undefined;
    }
    let nextIndex = 0;
    if (direction === 'decrement') {
      if (!isNullish(prevIndex)) {
        nextIndex = Math.max(prevIndex - 1, 0);
      } else {
        nextIndex = suggItems.length - 1;
      }
    } else {
      if (!isNullish(prevIndex) && !!suggItems) {
        nextIndex = Math.min(prevIndex + 1, suggItems.length - 1);
      }
    }
    if (suggItems[nextIndex].separator) {
      // this is a separator, it doesn't count when focus changes, keep going!
      return getNextIndex(suggItems, nextIndex, direction);
    }
    return nextIndex;
  }, []);
  const getNextSuggestionAndFocusIndices = useCallback((prevFocusedIndex, direction) => {
    if (!suggestionItems || !itemsNoSeparators) {
      return {
        nextFocusedIndex: undefined,
        nextSuggestionIndex: undefined
      };
    }
    const nextFocusedIndex = getNextIndex(itemsNoSeparators, prevFocusedIndex, direction);
    const prevSuggestionIndex = prevFocusedIndex ? suggestionItems.indexOf(itemsNoSeparators[prevFocusedIndex]) : undefined;
    const nextSuggestionIndex = getNextIndex(suggestionItems, prevSuggestionIndex, direction);
    return {
      nextFocusedIndex,
      nextSuggestionIndex
    };
  }, [suggestionItems, itemsNoSeparators, getNextIndex]);
  const onMouseDown = useCallback(() => {
    propsOnMouseDown?.();
  }, [propsOnMouseDown]);
  const handleKeyDown = useCallback(e => {
    // commitOnEnterInDialog (true by default), keeps the markEvent from happening thus triggering the default action.
    // Marking this event is needed when Input is in a Dialog so that Enter doesn't trigger the Dialog's default button
    if (commitOnEnterInDialog && e.key === 'Enter') {
      markEvent(e);
    }
    if (e.key === 'Escape') {
      closeSuggestions();
      const cancelHandled = propsOnCancel?.();
      if (shouldShowSuggestions || propsOnCancel && cancelHandled !== false) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    if (!shouldShowSuggestions && e.key !== 'Enter' && isPrintableKey(e.key) && propsSuggestionsDisplay !== 'never') {
      setShouldShowSuggestions(true);
      // prevent text input cursor from moving if key was space.
      // this means the space bar was the first key entered.
      if (propsValue === '' && propsSuggestionsDisplay === 'secondFocus' && e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    if (!shouldShowSuggestions || !suggestionItems || suggestionItems.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      const nextIndices = getNextSuggestionAndFocusIndices(focusedIndex, 'increment');
      setFocusedIndex(nextIndices.nextFocusedIndex);
    } else if (e.key === 'ArrowUp') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      const nextIndices = getNextSuggestionAndFocusIndices(focusedIndex, 'decrement');
      setFocusedIndex(nextIndices.nextFocusedIndex);
    }
  }, [closeSuggestions, commitOnEnterInDialog, focusedIndex, getNextSuggestionAndFocusIndices, propsOnCancel, propsSuggestionsDisplay, propsValue, shouldShowSuggestions, suggestionItems]);
  const handleChange = useCallback(e => {
    const liveValue = e.target.value;
    propsOnChange({
      value: liveValue
    });
    setFocusedIndex(undefined);
  }, [propsOnChange]);
  const handleKeyPress = useCallback(event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!isNullish(focusedIndex) && !!itemsNoSeparators && suggestionItems) {
        const suggestionIndex = suggestionItems.indexOf(itemsNoSeparators[focusedIndex]);
        const currentSuggestion = suggestionItems[suggestionIndex];
        const currentSuggestionText = currentSuggestion.text;
        const suggestionType = currentSuggestion.suggestionType;
        if (!!currentSuggestionText) {
          onSuggestionItemSelected?.({
            index: suggestionIndex
          });
          if (suggestionType && suggestionType !== 'catalog') {
            closeSuggestions();
          }
          return;
        }
      }
      closeSuggestions();
      propsOnCommit?.({
        reason: 'enter',
        value: propsValue
      });
    }
  }, [closeSuggestions, focusedIndex, suggestionItems, itemsNoSeparators, onSuggestionItemSelected, propsOnCommit, propsValue]);
  const handleMouseOver = useCallback(e => {
    setHasOver(true);
    const input = e.target;
    const content = input?.value;
    if (content?.length && isTruncated(input, content)) {
      input.setAttribute('title', content);
    } else {
      input.removeAttribute('title');
    }
  }, [setHasOver]);
  const handleMouseLeave = useCallback(() => setHasOver(false), []);
  const handleOnClick = useCallback(() => {
    // this is meant for when the search field isn't showing the suggestions
    // popover and the user clicks on the search field to show it.
    if (!shouldShowSuggestions && propsSuggestionsDisplay !== 'never') {
      setShouldShowSuggestions(true);
    }
  }, [propsSuggestionsDisplay, shouldShowSuggestions]);

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const dt = useDesignTokens();
  const styleTokenOverrideValues = useMemo(() => {
    const overrides = props.theme;
    const placeholder = props.placeholderTheme;
    let overrideValues = {};
    if (overrides) {
      overrideValues = {
        ...renderInlineCssVars(overrides({
          state: 'normal'
        }), 'input'),
        ...renderInlineCssVars(overrides({
          state: 'alert'
        }), 'input-alert'),
        ...renderInlineCssVars(overrides({
          state: 'warning'
        }), 'input-warning')
      };
    }
    if (placeholder) {
      overrideValues = {
        ...overrideValues,
        ...renderInlineCssVars(placeholder(), 'input-placeholder')
      };
    }
    return overrideValues;
  }, [props.theme, props.placeholderTheme]);
  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  // Outline Focus Indicator
  let outline;
  let border;
  if (dt.themeVariant !== 'high-contrast' && editable && !props.disabled && !isCustomFocusTurnedOn) {
    outline = 'none';
  }
  if ((dt.themeVariant === 'high-contrast' || props.disabled) && props.variant === 'composite') {
    border = 'transparent';
  }
  const style = {
    ...renderInlineCssVars(getInputThemeTokens(dt, props.status), 'input'),
    ...renderInlineCssVars({
      'pd-squish-s': dt.tokens['space-squish-s'],
      'mg-xs': dt.tokens['space-base-xs'],
      'bd-width': '0 0 1px 0',
      ...(!isNewTokenValueFlagEnabled && {
        'bd-width': dt.tokens['size-border-width-s']
      }),
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out'],
      'bottom-bd-inset-block-end': '1px'
    }, 'input'),
    ...renderInlineCssVars({
      outline,
      'noneditable-bd': border
    }, 'input-focus'),
    ...styleTokenOverrideValues,
    ...props.font
  };
  const idList = useListId();
  const listItemIds = useListItemId(suggestionItems ? suggestionItems.length : 0);
  const idValidationMessage = useValidationMessageId();
  const ariaDescribedBy = [];
  if (props.status) ariaDescribedBy.push(idValidationMessage);
  if (props.ariaDescribedBy) ariaDescribedBy.push(...props.ariaDescribedBy);
  const historyItemDoubleClickRef = useRef();
  const onListItemSuggestionSelected = useCallback(({
    index
  }) => {
    if (onHistoryItemDoubleClick) {
      logger.debug('history double click check is enabled');
      const suggestionIndex = noSeparatorIndexToSuggestionIndex(index);
      logger.debug(`suggestionIndex: ${suggestionIndex}`);
      if (suggestionIndex) {
        if (suggestionItems?.[suggestionIndex]?.suggestionType === 'history') {
          logger.debug('history item detected');
          // history items can be double-clicked to immediately execute a search, so we need to delay selecting the suggestion for enough time
          // to allow a double-click to be detected.
          if (historyItemDoubleClickRef.current) {
            logger.debug(`double-click detected on:  ${suggestionIndex}`);
            const [timeoutHandle, ix] = historyItemDoubleClickRef.current;
            historyItemDoubleClickRef.current = undefined;
            clearTimeout(timeoutHandle);
            if (ix === index) {
              const suggIx = noSeparatorIndexToSuggestionIndex(ix);
              if (suggIx !== undefined) {
                onHistoryItemDoubleClick({
                  index: suggIx
                });
                closeSuggestions();
              }
            }
            return;
          }
          const handle = setTimeout(() => {
            logger.debug(`entering timeout handler`);
            if (historyItemDoubleClickRef.current) {
              const [h, i] = historyItemDoubleClickRef.current;
              historyItemDoubleClickRef.current = undefined;
              clearTimeout(h);
              selectSuggestionHandler(i);
              logger.debug(`history item ${i} was single-clicked`);
            }
          }, DOUBLE_CLICK_DELAY);
          historyItemDoubleClickRef.current = [handle, index];
          return;
        }
      }
    }
    logger.debug(() => `single click detected on:  ${index} at ${Date.now()}`);
    selectSuggestionHandler(index);
  }, [selectSuggestionHandler, suggestionItems, noSeparatorIndexToSuggestionIndex, onHistoryItemDoubleClick, closeSuggestions]);
  let narratorMessage = '';
  if (itemsNoSeparators?.length === 0) {
    narratorMessage = localize('narrator.noresults.msg');
  } else if (shouldShowSuggestions) {
    if (itemsNoSeparators?.length === 1) narratorMessage = localize('narrator.result.msg');else {
      narratorMessage = localize('narrator.results.msg', {
        numResults: itemsNoSeparators?.length.toString() || ''
      });
    }
  }
  const directSpeech = useDirectSpeech();
  useEffect(() => {
    if (shouldShowSuggestions || itemsNoSeparators?.length === 0) {
      directSpeech?.notify(narratorMessage);
    }
  }, [directSpeech, shouldShowSuggestions, itemsNoSeparators?.length, narratorMessage]);
  const op = useMemo(() => {
    return /*#__PURE__*/_jsx(OptionPopper, {
      ref: suggestionListRef,
      focusedIndex: focusedIndex,
      listId: idList,
      isOpen: shouldShowSuggestions,
      referenceElementRef: refInput,
      displayListItemIcon: true,
      ulRef: refList,
      items: !!suggestionItems ? suggestionItems.map((s, i) => ({
        ...s,
        id: listItemIds[i]
      })) : [],
      onSelectFocusedItem: () => {
        if (!isNullish(focusedIndex)) {
          selectSuggestionHandler(focusedIndex);
        }
      },
      onSelected: onListItemSuggestionSelected,
      listItemFocusable: true,
      testId: postfixTestId(props.testId, 'suggestionPopper'),
      focusLockProps: {
        disabled: true
      },
      style: {
        marginBlockStart: '1px'
      },
      showSelectedPseudoFocus: true,
      ariaLabel: localize('search.suggestion.label')
    });
  }, [shouldShowSuggestions, suggestionListRef, focusedIndex, idList, refInput, refList, suggestionItems, selectSuggestionHandler, onListItemSuggestionSelected, props.testId, listItemIds, localize]);
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const iconAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container, isNewTokenValueFlagEnabled && cn.featureFlagEnabled, props.className),
    style: {
      ...style,
      ...props.style
    },
    onBlur: onBlur,
    onFocus: onFocus,
    onMouseDown: onMouseDown,
    "data-testid": props.testId,
    children: [/*#__PURE__*/_jsx("input", {
      style: props.font,
      tabIndex: props.tabindex,
      className: cx(cn.input, props.hideBorder && cn.hideBorder, props.status && cn.hasInputIcon, props.disabled && cn.disabled, !editable && cn.readonly, props.variant === 'composite' && props.status === undefined && !editable && cn.composite, props.variant === 'composite' && !editable && cn.nonEditableInput, isRTL && cn.rtl),
      "data-testid": postfixTestId(props.testId, 'input'),
      ref: setRefInput,
      value: propsValue,
      placeholder: !editable ? undefined : props.placeholder,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onKeyPress: handleKeyPress,
      onMouseEnter: handleMouseOver,
      onMouseLeave: handleMouseLeave,
      onClick: handleOnClick,
      onFocus: () => {
        setHasFocus(true);
      },
      type: 'text',
      spellCheck: "false",
      autoComplete: "off"
      // Set aria-autocomplete='list' here if there are suggestions displayed (for now).  This setting should be changed to be aria-autocomplete='both'
      // in the future when the highlighted "type-ahead" functionality is added to the Input (when only one filtered selection exists). This directive
      // is per the recent Accessibility Clinic review of Input (8/10/2021) with Jesse Sookne/Greg Kraus.
      ,
      "aria-autocomplete": shouldShowSuggestions ? 'list' : undefined,
      "aria-haspopup": shouldShowSuggestions ? true : undefined,
      "aria-disabled": props.disabled,
      "aria-invalid": props.status === 'alert' ? true : undefined,
      "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
      "aria-controls": shouldShowSuggestions ? idList : undefined,
      "aria-activedescendant": shouldShowSuggestions && !isNullish(focusedIndex) && noSeparatorIndexToSuggestionIndex(focusedIndex) !== undefined ? listItemIds[noSeparatorIndexToSuggestionIndex(focusedIndex)] : undefined,
      "aria-label": props.ariaLabel,
      "aria-labelledby": props.ariaLabelledBy,
      id: props.id,
      readOnly: !editable || props.disabled,
      "aria-readonly": !editable,
      maxLength: props.maxLength,
      "data-landmark-label": props.landmarkLabel,
      role: props.role
    }), props.status ? /*#__PURE__*/_jsx("span", {
      "aria-live": "off",
      role: "status",
      style: {
        width: '0'
      },
      children: /*#__PURE__*/_jsx("span", {
        className: cn.offscreen,
        id: idValidationMessage,
        children: localize('invalidInput.msg', {
          message: isCustomMessage(props.message) ? props.message.message : props.message
        })
      })
    }) : null, /*#__PURE__*/_jsx(AnimatePresence, {
      children: !hasFocus && props.status ? /*#__PURE__*/_jsx(motion.div, {
        className: cn.inputIcon,
        onMouseEnter: handleMouseOver,
        onMouseLeave: handleMouseLeave,
        variants: iconAnimation,
        initial: "closed",
        animate: "open",
        exit: "exit",
        children: /*#__PURE__*/_jsx(Icon, {
          icon: props.status === 'alert' ? ErrorStatus : WarningStatus,
          color: props.status
        })
      }) : null
    }), op, props.status ? /*#__PURE__*/_jsx(MessagePopover, {
      status: props.status,
      referenceElementRef: refInput,
      ...(isCustomMessage(props.message) ? props.message : {
        message: props.message
      }),
      isOpen: hasFocus || hasHover,
      testId: postfixTestId(props.testId, 'messagePopper')
    }) : null]
  });
});
export const getInputThemeTokens = (dt, status) => {
  if (dt.enableNovaTokens) {
    if (status) {
      return {
        'input-fg': dt.tokens[`color-action-text-control-${status}`],
        'input-bg': dt.tokens[`color-action-background-control-${status}`],
        'input-bd': dt.tokens[`color-action-border-bottom-control-${status}`],
        'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
        'selection-bg': dt.tokens['color-highlight-text'],
        'selection-fg': dt.tokens['color-highlight-text-inverse'],
        'disabled-opacity': 1,
        'disabled-fg': dt.tokens['color-action-text-control-disabled'],
        'disabled-bd': dt.tokens['color-action-border-control-disabled'],
        'disabled-bg': dt.tokens['color-action-background-control-disabled'],
        'noneditable-bg': dt.tokens['color-action-background-control-read-only'],
        'noneditable-bd': dt.tokens['color-action-border-control-read-only']
      };
    } else {
      return {
        fg: dt.tokens['color-action-text-control-normal'],
        bg: dt.tokens['color-action-background-control-normal'],
        bd: dt.tokens['color-action-border-bottom-control-normal'],
        'disabled-opacity': 1,
        'disabled-fg': dt.tokens['color-action-text-control-disabled'],
        'disabled-bd': dt.tokens['color-action-border-control-disabled'],
        'disabled-bg': dt.tokens['color-action-background-control-disabled'],
        'hover-fg': dt.tokens['color-action-text-control-hover'],
        'hover-bg': dt.tokens['color-action-background-control-hover'],
        'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
        'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
        'selection-bg': dt.tokens['color-highlight-text'],
        'selection-fg': dt.tokens['color-highlight-text-inverse'],
        'focus-bd': dt.tokens['color-action-border-bottom-control-selected'],
        'focus-bg': dt.tokens['color-action-background-control-selected'],
        'focus-fg': dt.tokens['color-action-text-control-selected'],
        'readonly-bg': dt.tokens['color-action-background-control-read-only'],
        'readonly-bd': dt.tokens['color-action-border-control-read-only'],
        'readonly-fg': dt.tokens['color-action-text-control-read-only'],
        'noneditable-bg': dt.tokens['color-action-background-control-read-only'],
        'noneditable-bd': dt.tokens['color-action-border-control-read-only'],
        'noneditable-fg': dt.tokens['color-action-text-control-read-only']
      };
    }
  } else {
    // legacy tokens
    if (status) {
      const statusTokens = {
        ...dt.stateStatus[status],
        'selection-bg': dt.reportTokens.text.selection.bg,
        'selection-fg': dt.reportTokens.text.selection.fg
      };
      if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
        return {
          ...statusTokens,
          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5
        };
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25
          };
        case 'high-contrast':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5
          };
        default:
          return {
            ...statusTokens,
            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3
          };
      }
    } else {
      if (dt.baseReportThemeName === 'highcontrast2017' || dt.baseReportThemeName === 'highcontrast2018') {
        return {
          bd: dt.c.white,
          bg: dt.c.black,
          fg: dt.c.white,
          'selection-bg': dt.reportTokens.text.selection.bg,
          'selection-fg': dt.reportTokens.text.selection.fg,
          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5,
          'focus-bd': dt.c.p0
        };
      }
      switch (dt.themeVariant) {
        case 'dark':
          return {
            bd: setAlpha(dt.c.white, 0.3),
            bg: dt.c.p_3,
            fg: dt.c.white,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25,
            'focus-bd': dt.c.p0
          };
        case 'high-contrast':
          return {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5,
            'focus-bd': dt.c.p3
          };
        default:
          return {
            bd: dt.c.n1,
            bg: dt.c.white,
            fg: dt.c.n_3,
            'selection-bg': dt.reportTokens.text.selection.bg,
            'selection-fg': dt.reportTokens.text.selection.fg,
            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3,
            'focus-bd': dt.c.p0
          };
      }
    }
  }
};

// https://www.w3.org/TR/uievents-key/#named-key-attribute-values
// this is not quite exhaustive, but I want to be careful about excluding international or composed characters
const isPrintableKey = key => {
  return !['Alt', 'AltGraph', 'CapsLock', 'Control', 'Fn', 'FnLock', 'Meta', 'NumLock', 'ScrollLock', 'Shift', 'Symbol', 'SymbolLock', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', 'Meta', 'NumLock', 'ScrollLock', 'Shift', 'Symbol', 'SymbolLock', 'Copy', 'CrSel', 'Cut', 'Delete', 'EraseEof', 'ExSel', 'Insert', 'Accept', 'Again', 'Attn', 'Cancel', 'ContextMenu', 'Escape', 'Execute', 'Find', 'Help', 'Pause', 'Play', 'Props', 'Select', 'ZoomIn', 'ZoomOut', 'BrightnessDown', 'BrightnessUp', 'Eject', 'LogOff', 'Power', 'WakeUp', 'ChannelDown', 'ChannelUp', 'Close', 'MailForward', 'MailReply', 'MailSend', 'MediaClose', 'MediaFastForward', 'MediaPause', 'MediaPlay', 'MediaPlayPause', 'MediaRecord', 'MediaRewind', 'MediaStop', 'MediaTrackNext', 'MediaTrackPrevious', 'New', 'Open', 'Print', 'Save', 'SpellCheck', 'AudioBalanceLeft', 'AudioBalanceRight', 'AudioBassBoostDown', 'AudioBassBoostToggle', 'AudioBassBoostUp', 'AudioFaderFront', 'AudioFaderRear', 'AudioSurroundModeNext', 'AudioTrebleDown', 'AudioTrebleUp', 'AudioVolumeDown', 'AudioVolumeUp', 'AudioVolumeMute', 'MicrophoneToggle', 'MicrophoneVolumeDown', 'MicrophoneVolumeUp', 'MicrophoneVolumeMute', 'SpeechCorrectionList', 'SpeechInputToggle', 'LaunchApplication1', 'LaunchApplication2', 'LaunchCalendar', 'LaunchContacts', 'LaunchMail', 'LaunchMediaPlayer', 'LaunchMusicPlayer', 'LaunchPhone', 'LaunchScreenSaver', 'LaunchSpreadsheet', 'LaunchWebBrowser', 'LaunchWebCam', 'LaunchWordProcessor', 'BrowserBack', 'BrowserFavorites', 'BrowserForward', 'BrowserHome', 'BrowserRefresh', 'BrowserSearch', 'BrowserStop', 'AppSwitch', 'Call', 'Camera', 'CameraFocus', 'EndCall', 'GoBack', 'GoHome', 'HeadsetHook', 'LastNumberRedial', 'Notification', 'MannerMode', 'VoiceDial', 'TV', 'TV3DMode', 'TVAntennaCable', 'TVAudioDescription', 'TVAudioDescriptionMixDown', 'TVAudioDescriptionMixUp', 'TVContentsMenu', 'TVDataService', 'TVInput', 'TVInputComponent1', 'TVInputComponent2', 'TVInputComposite1', 'TVInputComposite2', 'TVInputHDMI1', 'TVInputHDMI2', 'TVInputHDMI3', 'TVInputHDMI4', 'TVInputVGA1', 'TVMediaContext', 'TVNetwork', 'TVNumberEntry', 'TVPower', 'TVRadioService', 'TVSatellite', 'TVSatelliteBS', 'TVSatelliteCS', 'TVSatelliteToggle', 'TVTerrestrialAnalog', 'TVTerrestrialDigital', 'TVTimer'].includes(key);
};
//# sourceMappingURL=CatalogInput.js.map